// web/src/tutorial/detail/wind-rise.ts
// WindRise · 软件界面详细说明（逐个页签、逐个参数）
//
// 依据：G:\wind_rise_rust
//   src/ui/app.rs（顶栏：三个页签 / 主题 / 语言 / 帮助）、auth_panel.rs（授权界面）、
//   about.rs（关于对话框）、avatar_panel.rs + avatar_display.rs + avatar_manager.rs（角色设置页）、
//   midi_panel.rs（MIDI处理页）、execution_panel.rs（执行操作页）、components.rs（卡片 / 徽章 / 状态块）、
//   data.rs（各参数默认值）、src/animation/timeline.rs 与 animator.rs（渐变时长与换气的作用）、
//   src/midi/midi_processor.rs（量化 / 通道 / 音域 / 修剪的实际语义）；
//   asset/i18n/zh-CN.json（界面原文，反引号里的标签与之逐字一致）。
//
// 本产品只有 3 张界面截图（角色设置 / MIDI处理 / 执行操作），按页签一一对应；
// 授权界面、关于对话框、角色编辑弹窗没有截图，正文照写并在该处标注「截图待补」。

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const windRiseDetail: TutorialDoc = {
  key: "wind_rise",
  slug: "wind-rise",
  kind: "detail",
  icon: "🎷",
  title: l("WindRise 软件界面详细说明", "WindRise UI reference"),
  subtitle: l(
    "把 Wind Rise 的三屏界面从上到下拆开讲：每个分区负责什么、每个按钮点下去会发生什么、每个可调参数控制什么，以及什么时候需要调整。",
    "Wind Rise's three screens, taken apart from top to bottom: what each section does, what every button actually does, and what each adjustable parameter controls — plus when you would want to touch it.",
  ),
  readMinutes: 12,
  video: {
    hidden: true,
    caption: l("本页不内嵌视频", "No embedded video on this page"),
  },
  images: [
    {
      src: "/assets/image/products/wind-rise/CN/角色设置.png",
      srcEn: "/assets/image/products/wind-rise/EN/Avatar Setting.png",
      label: l("⚙ 角色设置", "⚙ Avatar Settings"),
      width: 1282,
      height: 954,
    },
    {
      src: "/assets/image/products/wind-rise/CN/MIDI处理.png",
      srcEn: "/assets/image/products/wind-rise/EN/MIDI Processing.png",
      label: l("🎵 MIDI处理", "🎵 MIDI Processing"),
      width: 1282,
      height: 954,
    },
    {
      src: "/assets/image/products/wind-rise/CN/执行操作.png",
      srcEn: "/assets/image/products/wind-rise/EN/Execution.png",
      label: l("▶ 执行操作", "▶ Execution"),
      width: 1282,
      height: 954,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "how-to-read",
      title: l("这篇怎么用", "How to use this page"),
      blocks: [
        {
          kind: "p",
          text: l(
            "这一页是**参数手册**：按软件顶栏的页签顺序，一屏一屏地把界面拆开讲。想先跑通一次完整流程、不打算碰任何参数，请看[快速上手](/quickstart/wind-rise.html)。",
            "This page is the **parameter reference**: it walks the app screen by screen, following the tab order along the top. To get one full run done without touching a single parameter, read the [Quick Start](/quickstart/wind-rise.html) instead.",
          ),
        },
        {
          kind: "p",
          text: l(
            "正文里出现的控件名都与界面实物逐字对应（卡片标题、参数名、按钮名），大小写、括号和空格都按界面原样保留，照着找就能定位到。",
            "Everything in backticks is text that really appears in the interface: card titles, parameter names, button labels, punctuation and all. Look for it on screen and you will find it.",
          ),
        },
        {
          kind: "p",
          text: l(
            "每个参数都写了三件事：**管什么**、**取值范围与默认值**、**什么时候你会想动它**。范围与默认值都照程序里的实际取值写，没有四舍五入过。",
            "Every parameter is described three ways: **what it controls**, **its range and default**, and **when you would reach for it**. Ranges and defaults are copied from the app's actual values, unrounded.",
          ),
        },
        {
          kind: "note",
          text: l(
            "Wind Rise 只有三屏主界面（⚙ 角色设置、🎵 MIDI处理、▶ 执行操作），每一屏都配了截图。除此之外还有激活完整版的对话框、🔓 解锁全部功能 页签、关于 对话框和角色编辑弹窗，它们没有单独截图，正文里会注明。",
            "Wind Rise has exactly three main screens, each with a screenshot: ⚙ 角色设置 / Avatar Settings, 🎵 MIDI处理 / MIDI Processing and ▶ 执行操作 / Execution. Beyond those there are the full-version activation dialog, the 🔓 解锁全部功能 / Unlock All Features tab, the 关于 / About dialog and the performer editing windows; those have no screenshots of their own, and the text says so where they come up.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "app-shell",
      title: l("启动与顶栏", "Launch and the top bar"),
      blocks: [
        {
          kind: "p",
          text: l(
            "装好解压、双击就能用：**免费版不需要授权码**，启动直接进主界面，内置演奏者可以直接跑完整个流程。想把软件升级成完整版，在 帮助 → 激活完整版 里填入作者发给你的授权码（不打这个对话框，也可以点顶栏的 🔓 解锁全部功能 页签，里面同样有激活入口和购买地址）。",
            "Unzip, double-click and you are in: the **free edition needs no license key** — it opens straight into the main window and the bundled performers can run the whole workflow. To switch to the full version, use 帮助 / Help → 激活完整版 / Activate Full Version and paste the key the author sent you (the 🔓 解锁全部功能 / Unlock All Features tab on the top bar has the same entry plus the purchase link).",
          ),
        },
        {
          kind: "ul",
          items: [
            l(
              "激活对话框里会显示本机的 机器码:（相当于这台电脑的身份证），点旁边的 📋 复制 拷走，连同购买信息发给作者换回 授权码:。",
              "The activation dialog shows this machine's 机器码: / Machine Code — this computer's ID. Press 📋 复制 / Copy beside it, send it to the author along with your purchase details, and you get a 授权码: / License Key back.",
            ),
            l(
              "验证通过后授权码会存进软件目录的 .user 文件，以后启动直接是完整版，不用再填。",
              "Once it passes, the key is stored in a .user file next to the app; later launches start as the full version with nothing to re-enter.",
            ),
            l(
              "填错会就地红字提示 授权码无效，请重新输入；机器码取不到时（极少数网卡环境）界面会直接说明本机无法完成授权验证。",
              "A wrong key answers in red right there: 授权码无效，请重新输入 / Invalid license key, please re-enter. If the machine ID cannot be read at all (rare network setups), the screen says outright that this device cannot be authorized.",
            ),
          ],
        },
        {
          kind: "p",
          text: l(
            "进到主界面之后，三屏共用一个顶栏：左边是 Wind Rise 标题和 ⚙ 角色设置 / 🎵 MIDI处理 / ▶ 执行操作 三个页签，免费版还会多一个 🔓 解锁全部功能 页签（激活成功后它就不见了）；右边依次是主题开关 🌙 深色 / ☀ 浅色、语言 菜单（第一项 跟随系统，下面列出可用语言）和 帮助 菜单（里面有 关于、购买完整版、激活完整版）。",
            "Inside the main window all three screens share one top bar: the Wind Rise title and the ⚙ 角色设置 / Avatar Settings, 🎵 MIDI处理 / MIDI Processing and ▶ 执行操作 / Execution tabs on the left, plus a 🔓 解锁全部功能 / Unlock All Features tab shown only in the free edition (it disappears once activated). On the right sit the 🌙 深色 / Dark and ☀ 浅色 / Light theme switch, the 语言 / Language menu (first entry 跟随系统 / Follow System, then the available languages) and the 帮助 / Help menu (关于 / About, 购买完整版 / Buy Full Version, 激活完整版 / Activate Full Version).",
          ),
        },
        {
          kind: "ul",
          items: [
            l(
              "🌙 深色 / ☀ 浅色 —— 只换配色。本文截图是深色主题下的样子，切浅色不影响任何生成结果。",
              "🌙 深色 / Dark and ☀ 浅色 / Light — colour scheme only. The screenshots here are from the dark theme; switching to light changes nothing about the output.",
            ),
            l(
              "语言 —— 跟随系统 让界面跟着操作系统走，也可以在这里手动钉死一种语言。选择会被记住，下次启动仍然生效。",
              "语言 / Language — 跟随系统 / Follow System follows the operating system, or pin one language by hand. The choice is remembered across launches.",
            ),
            l(
              "帮助 → 关于 —— 打开 关于 Wind Rise 对话框，里面有软件说明和版本号，点 关闭 关掉。同一个菜单里还有 购买完整版 与 激活完整版 两项。",
              "帮助 / Help → 关于 / About — opens the 关于 Wind Rise / About Wind Rise dialog, with a short description and the version number; 关闭 / Close dismisses it. The same menu also holds 购买完整版 / Buy Full Version and 激活完整版 / Activate Full Version.",
            ),
            l(
              "🔓 解锁全部功能 —— 只有免费版才有的页签：里面写着免费版已经能做什么、完整版多了什么、购买地址，以及一个 🔑 我有授权码 按钮。激活成功后这个页签会自己消失。",
              "🔓 解锁全部功能 / Unlock All Features — a tab that exists only in the free edition: it lists what the free edition already does, what the full version adds, the purchase link, and a 🔑 我有授权码 / I Have a Key button. It disappears on its own once the app is activated.",
            ),
          ],
        },
        {
          kind: "note",
          text: l(
            "**关于 Wind Rise 对话框也没有单独截图（待补）。** 它里面除了软件说明和版本号之外没有任何开关，打开看看就可以关掉。",
            "**The 关于 Wind Rise / About Wind Rise dialog has no screenshot of its own either (still to come).** It holds nothing but a short description and the version number, so open it, read it and dismiss it.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-avatar",
      title: l(
        "⚙ 角色设置：决定「谁在吹」",
        "⚙ 角色设置 / Avatar Settings: who is playing",
      ),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一屏管两件事：**从现有演奏者里挑一个**，以及**维护演奏者名单**。页顶那行灰底提示写得很直白：管理吹奏角色配置，并设置指法与动画生成所需的全局参数。 选中的演奏者决定了用哪套指法、哪套口型形态键、以及吹的是哪件乐器。",
            "This screen does two jobs: **pick one of the performers you have**, and **maintain the performer list**. The grey banner at the top says it plainly: 管理吹奏角色配置，并设置指法与动画生成所需的全局参数。 / Manage wind performer configs and set the global parameters used for fingering and animation generation. Your selection decides which fingering set, which lip shape keys and which instrument get used.",
          ),
        },
        {
          kind: "p",
          text: l(
            "版面从左到右两列：左列是 01 角色选择、02 角色概览、03 角色参数 三张卡片，右列是 04 角色管理。日常使用你只会动 01，偶尔动一下 03 里的一项；02 是给你核对用的，04 是给你自己做的演奏者用的。",
            "The layout is two columns: 01 角色选择 / Avatar Selection, 02 角色概览 / Avatar Overview and 03 角色参数 / Avatar Parameters on the left, 04 角色管理 / Avatar Management on the right. Day to day you touch 01, occasionally one item inside 03, never 02 (it is there to check against) and never 04 (that is for performers you build yourself).",
          ),
        },
      ],
      subs: [
        {
          id: "avatar-select",
          title: l(
            "01 角色选择：挑一位演奏者",
            "01 角色选择 / Avatar Selection: pick a performer",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这张卡片里只有一个下拉菜单。点开它，里面是软件读到的全部演奏者；选中谁，02 角色概览、03 角色参数 和右边的角色图片立刻跟着换成谁。",
                "The card holds a single drop-down. Open it to see every performer the app found; whichever you pick immediately updates 02 角色概览 / Avatar Overview, 03 角色参数 / Avatar Parameters and the portrait on the right.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "**演奏者从哪来。** 软件读的是自己目录下的 asset/avatars/avatars.json。每条记录指向一个 .wind 配置文件（由 Blender 插件导出）和一张头像图片。",
                  "**Where performers come from.** The app reads asset/avatars/avatars.json in its own folder. Each entry points at a .wind config file (exported from the Blender plugin) and a portrait image.",
                ),
                l(
                  "**默认选的是谁。** 打开这一屏时会自动选中名单里的第一位，并把它的音域填进 03 角色参数 的 范围参数。截图里第一位是 七七_blender。",
                  "**What is selected by default.** Opening the screen selects the first entry in the list and copies its range into the 范围参数 / Range Parameters block under the 03 角色参数 / Avatar Parameters card. In the screenshot the first entry is 七七_blender.",
                ),
                l(
                  "**一个都没有的时候。** 下拉菜单的位置会换成一句说明：未加载到角色配置 / 当前 asset/avatars 目录为空，或尚未添加角色。 这时先去 04 角色管理 里 新增角色，或者把别人做好的 .wind 放进来。",
                  "**When there are none.** The drop-down is replaced by two lines. 未加载到角色配置 / No avatars loaded, then 当前 asset/avatars 目录为空，或尚未添加角色。 / The asset/avatars directory is empty, or no avatars have been added yet. Either add one through 新增角色 / Add Avatar in the 04 角色管理 / Avatar Management card, or drop in a .wind someone else built.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              title: l(
                "选演奏者其实就是选乐器",
                "Choosing a performer is choosing an instrument",
              ),
              text: l(
                "管乐的指法和音域都长在乐器上，所以换个演奏者往往就是换件乐器。选之前先扫一眼 02 角色概览 里的 乐器，它写的是 chinese_dizi 这类内部名称。",
                "Fingering and range live on the instrument, so switching performer usually means switching instrument. Glance at the 乐器 / Instrument row in the 02 角色概览 / Avatar Overview card first — it shows an internal name such as chinese_dizi.",
              ),
            },
          ],
        },
        {
          id: "avatar-overview",
          title: l(
            "02 角色概览：核对这位演奏者",
            "02 角色概览 / Avatar Overview: check who you picked",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这一块全是只读信息，来自你选中的那一条记录。生成之前扫一眼，能挡掉大部分「跑出来完全不对」的情况。",
                'This block is read-only and comes from the entry you selected. A glance before generating heads off most of the "the result is completely wrong" cases.',
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "名称 —— 演奏者的名字，也是输出文件名里的 {角色} 那一部分会用到的东西。",
                  "名称 / Name — the performer's name, which is also what the {角色} / {performer} part of the output file names is built from.",
                ),
                l(
                  "乐器 —— 乐器类型，指法算法按它选指法表。**这一行是这一屏最值得看一眼的。**",
                  "乐器 / Instrument — the instrument type; the fingering algorithm picks its table from this. **This is the one line on this screen worth a look.**",
                ),
                l(
                  "音域 —— 这件乐器吹得出来的音高范围（MIDI 音高号）。截图里的笛子是 60 ~ 91。管乐音域普遍很窄，这是后面会自动移调的原因。",
                  "音域 / Range — the pitch range this instrument can actually play (MIDI note numbers). The flute in the screenshot is 60 ~ 91. Wind ranges are narrow, which is why transposition happens later on.",
                ),
                l(
                  "配置 —— 这位演奏者的 .wind 文件路径，例如 asset/avatars/七七_flute.wind。指法点位、口型形态键名单全在里面。",
                  "配置 / Config — the path to this performer's .wind file, for example asset/avatars/七七_flute.wind. It holds the fingering positions and the lip shape-key names.",
                ),
                l(
                  "图片 —— 头像图片路径。这一项只有在配置里写了图片时才会出现，没写就整行不显示。",
                  "图片 / Image — the portrait path. It only appears when the entry defines an image; otherwise the row is absent.",
                ),
              ],
            },
            {
              kind: "note",
              text: l(
                "这里改不了任何东西。数字不对就去 04 角色管理 的 编辑角色，或者去 Blender 插件里重新导出 .wind。",
                "Nothing here is editable. If a value is wrong, go to 编辑角色 / Edit Avatar under the 04 角色管理 / Avatar Management card, or re-export the .wind from the Blender plugin.",
              ),
            },
          ],
        },
        {
          id: "avatar-params",
          title: l(
            "03 角色参数：这块最容易调出问题",
            "03 角色参数 / Avatar Parameters: the one card you can tune",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这张卡片分两个小块：时序参数 管「动作走得多快」，范围参数 管「音域记的是多少」。四个参数都在这里，其中真正影响成片观感的只有第一个。",
                "The card has two sub-blocks: 时序参数 / Timing Parameters governs how fast the motion travels, 范围参数 / Range Parameters records the range. Four parameters live here, but only the first really changes how the result looks.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "渐变时长(秒) —— **范围 0.05 ~ 2.0，默认 0.50，每格 0.05。** 它决定每个音在结束前多久开始朝下一个音的位置移动：程序取「这个音符时长的 1/3」和「渐变时长」里更小的那个，从那一刻开始过渡，一直过渡到下一个音头。乐器按键的释放回弹也按它的四分之一算。",
                  "渐变时长(秒) / Transition Duration (s) — **range 0.05 to 2.0, default 0.50, in steps of 0.05.** It decides how early each note starts moving toward the next position: the app takes whichever is smaller, one third of that note's length or the transition duration, and glides from there into the next attack. The instrument keys' release spring-back is a quarter of the same value.",
                ),
                l(
                  "**调大会怎样：** 过渡开始得更早、行程更长，手部与乐器的动作更连贯柔软，长音之间尤其自然；代价是快速乐句里动作还没到位就进了下一个音，看起来发糊、发黏。",
                  "**Turn it up:** the move starts earlier and lasts longer, so hands and instrument travel more smoothly — especially nice between long notes. The cost is that fast passages are still in motion when the next note arrives, which reads as smeared and sticky.",
                ),
                l(
                  "**调小会怎样：** 大部分时间停在原位、临近下一个音才「啪」地到位，快句更清楚干净；代价是慢曲子会显得一顿一顿的，像机械手。",
                  "**Turn it down:** the pose holds and then snaps into place right before the next note, which keeps fast passages crisp. The cost is that slow pieces look steppy, almost robotic.",
                ),
                l(
                  "**什么时候动它：** 慢速抒情曲可以试 0.8 ~ 1.2；快速吐音、密集跑句可以试 0.15 ~ 0.30。第一次用保持 0.50 就好。",
                  "**When to touch it:** try 0.8–1.2 for slow lyrical pieces, 0.15–0.30 for fast tonguing and dense runs. On a first run, leave it at 0.50.",
                ),
                l(
                  "最小音高 / 最大音高 —— **范围都是 0 ~ 127 的整数**，默认跟随你选中的那位演奏者：从它的 .wind 里读出来（截图里是 60 和 91）。改一下选中的演奏者，这两个数字会跟着一起换。",
                  "最小音高 / Min Note and 最大音高 / Max Note — **whole numbers from 0 to 127**, defaulting to whatever the selected performer's .wind declares (60 and 91 in the screenshot). Switching performer moves both numbers with it.",
                ),
                l(
                  "这两个框是给**你核对与记录**用的：它们会显示在 ▶ 执行操作 的 参数 里，方便你一眼看出这次生成用的是哪件乐器的音域。当前版本的指法生成会自己按乐器的物理音域算最佳移调偏移，所以把这两个数字改大改小都不会改变生成结果——别指望用它来「限制只生成某一段音高」。",
                  "These two boxes are for **checking and recording**: they are echoed under 参数 / Parameters on ▶ 执行操作 / Execution, so you can see at a glance which instrument's range this run used. The current fingering stage computes the optimal transposition from the instrument's physical range on its own, so widening or narrowing these numbers does not change the result — do not expect them to limit which pitches get generated.",
                ),
              ],
            },
            {
              kind: "note",
              text: l(
                "界面发现 最小音高 比 最大音高 大时会自动把两者对调，所以你填反了也不会报错，只是白填一次。",
                "If 最小音高 / Min Note ends up above 最大音高 / Max Note, the app swaps them for you — no error, you just filled them in the wrong way round for nothing.",
              ),
            },
            {
              kind: "callout",
              tone: "info",
              title: l(
                "换气相关的数字不在界面上",
                "The breath numbers are not on screen",
              ),
              text: l(
                "软件会自动在长乐句之间找间隙安排换气（口型提前归零、留一小段静默）。这套时长是程序里的固定值，界面上没有对应控件，所以你在这一屏找不到它。",
                "The app places breaths in the gaps between long phrases by itself, zeroing the lips slightly early and leaving a short silence. Those durations are fixed inside the program with no on-screen control, which is why you will not find them here.",
              ),
            },
          ],
        },
        {
          id: "avatar-manage",
          title: l(
            "04 角色管理：新增、编辑、删除演奏者",
            "04 角色管理 / Avatar Management: add, edit and remove performers",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "右列这张卡片上面是当前演奏者的图片预览（没有图片时显示 无图片 的灰块），下面是三个按钮。新增角色 和 编辑角色 打开同一个表单窗口，只是标题和预填内容不同。",
                "The right-hand card shows a preview of the current performer on top (a grey 无图片 / No Image block when there is none) and three buttons below. 新增角色 / Add Avatar and 编辑角色 / Edit Avatar open the same form window; only the title and the pre-filled values differ.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "新增角色 —— 打开空白表单，乐器默认填 flute，音域默认取当前面板上的数字。",
                  "新增角色 / Add Avatar — opens an empty form with the instrument pre-filled as flute and the range taken from the current panel values.",
                ),
                l(
                  "编辑角色 —— 打开表单并填入当前选中演奏者的全部字段。列表为空时这个按钮不做事。",
                  "编辑角色 / Edit Avatar — opens the form filled with the selected performer's fields. With an empty list the button does nothing.",
                ),
                l(
                  '删除角色 —— 弹出 确认删除角色 窗口，问的是 确定要删除角色 "名字" 吗？，并提醒 该操作会移除 avatars.json 中的条目，并尝试删除对应的 .wind 文件。 确认按钮是 确认删除，旁边是 取消。**删掉 .wind 是不可撤销的**，删前确认这个文件你已经别处留了备份。',
                  '删除角色 / Delete Avatar — opens the 确认删除角色 / Confirm Delete Avatar window asking 确定要删除角色 "名字" 吗？ / Are you sure you want to delete avatar "name"? and warning that 该操作会移除 avatars.json 中的条目，并尝试删除对应的 .wind 文件。 / This will remove the entry from avatars.json and attempt to delete the corresponding .wind file. The buttons are 确认删除 / Confirm Delete and 取消 / Cancel. **Deleting the .wind cannot be undone**, so make sure you have a copy elsewhere first.',
                ),
              ],
            },
            {
              kind: "p",
              text: l(
                "表单窗口里有四个字段，每个都配了说明：",
                "The form window has four fields, each with its own hint:",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "名称 —— 演奏者在下拉菜单里显示的名字。不能留空，也不能和已有的重名（会分别报 角色名称不能为空 / 角色名称已存在）。",
                  "名称 / Name — the name shown in the drop-down. It cannot be empty and cannot duplicate an existing one. The app answers 角色名称不能为空 / Avatar name cannot be empty, or 角色名称已存在 / Avatar name already exists.",
                ),
                l(
                  "乐器 —— 乐器类型文字，例如 chinese_dizi。它决定用哪套指法。",
                  "乐器 / Instrument — the instrument type string, such as chinese_dizi. It decides which fingering set is used.",
                ),
                l(
                  "图片 —— 头像路径，旁边 浏览 按钮可以挑文件（支持 png / jpg / jpeg / webp）。保存时图片会被复制到软件目录的 asset/img/ 下。",
                  "图片 / Image — the portrait path; the 浏览 / Browse button picks a file (png / jpg / jpeg / webp). On save the image is copied into asset/img/ next to the app.",
                ),
                l(
                  "配置 —— .wind 文件路径，旁边 浏览 按钮可以挑文件（支持 wind / json）。**选中一个 .wind 之后，乐器 和音域会自动从它里面读出来填好**，所以正确的做法是先挑 配置，再回头核对 乐器。",
                  "配置 / Config — the .wind path; the 浏览 / Browse button picks a file (wind / json). **Picking a .wind automatically fills in the 乐器 / Instrument field and the range from that file**, so the right order is to choose the 配置 / Config field first and then check 乐器.",
                ),
              ],
            },
            {
              kind: "p",
              text: l(
                "窗口底部提示 保存时会把角色配置写入 asset/avatars，并更新 avatars.json。，下面两个按钮是 保存 和 取消。保存 成功后列表会重新加载，并把新角色直接选中。",
                "The hint at the bottom reads 保存时会把角色配置写入 asset/avatars，并更新 avatars.json。 / On save, the avatar config will be written to asset/avatars and avatars.json will be updated. The two buttons are 保存 / Save and 取消 / Cancel. A successful save reloads the list and selects the new performer straight away.",
              ),
            },
            {
              kind: "note",
              text: l(
                "**新增角色 / 编辑角色 / 删除角色 打开的都是弹窗，本文没有它们的截图（待补）** —— 上面的字段名都按界面原文写出，照着找即可。",
                "**The 新增角色 / Add Avatar, 编辑角色 / Edit Avatar and 删除角色 / Delete Avatar buttons all open dialogs, and this page has no screenshots of them (still to come).** The field names above are the interface's own wording, so you can find them on screen.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "软件不生成 .wind",
                "The app does not create .wind files",
              ),
              text: l(
                ".wind 是 Blender 插件导出的。这个软件只负责读它、把它登记进 avatars.json。所以在 配置 里填一个不存在的路径，保存不会报错，但选到这位演奏者时就不对了。",
                ".wind files come out of the Blender plugin. This app only reads them and registers them in avatars.json. Point the 配置 / Config field at a path that does not exist and the save still succeeds — but selecting that performer then goes wrong.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-midi",
      title: l(
        "🎵 MIDI处理：决定「吹的是什么」",
        "🎵 MIDI处理 / MIDI Processing: what gets played",
      ),
      image: { image: 1 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一屏把一首原始 MIDI 整理成指法生成能用的输入。页顶提示说明了它的定位：选择 MIDI、扫描轨道并完成预处理。该页负责把原始文件整理成后续指法生成可用的数据输入。",
            "This screen turns a raw MIDI file into input the fingering stage can use. Its banner states the job — 选择 MIDI、扫描轨道并完成预处理。该页负责把原始文件整理成后续指法生成可用的数据输入。 / Select a MIDI file, scan its tracks and finish preprocessing; this page turns the raw file into usable input for the fingering stage.",
          ),
        },
        {
          kind: "p",
          text: l(
            "左列是 01 文件与扫描 和 02 参数设置，右列是 03 轨道选择。操作顺序是自上而下：先把文件弄进来，再设置参数，最后扫描并确认轨道。",
            "The left column holds the 01 文件与扫描 / File & Scan and 02 参数设置 / Parameter Settings cards; the right column holds 03 轨道选择 / Track Selection. Work top to bottom: bring the file in, set the parameters, then scan and confirm the tracks.",
          ),
        },
        {
          kind: "callout",
          tone: "warn",
          title: l("先设参数，再点扫描", "Set parameters before scanning"),
          text: l(
            "02 参数设置 里的 通道、FPS、量化 是 MIDI 解析的输入。改完它们，已经扫出来的轨道列表不会自己更新——**要再点一次 扫描 MIDI 信息**。",
            "The 通道 / Channel, FPS and 量化 / Quantization values in the 02 参数设置 / Parameter Settings card are inputs to MIDI parsing. Changing them does not refresh the track list you already scanned — **press 扫描 MIDI 信息 / Scan MIDI Info again.**",
          ),
        },
      ],
      subs: [
        {
          id: "midi-file",
          title: l(
            "01 文件与扫描：把 MIDI 拿进来",
            "01 文件与扫描 / File & Scan: bring the MIDI in",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这张卡片上有两个按钮、一块当前文件信息和一片拖放区。",
                "This card has two buttons, a current-file block and a drop area.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "浏览 MIDI 文件 —— 打开文件选择框，只能挑 .mid / .midi。选好之后 当前文件 块出现，里面是 文件名 和 完整路径 两行。",
                  "浏览 MIDI 文件 / Browse MIDI File — opens a file picker limited to .mid / .midi. Once chosen, the 当前文件 / Current File block appears with its 文件名 / File Name and 完整路径 / Full Path rows.",
                ),
                l(
                  "扫描 MIDI 信息 —— 真正去读这个文件，把轨道清单填到右边的 03 轨道选择 里，同时把轨道摘要写进 output/current_midi_info.txt。**还没选文件就点它，会提示 请先选择 MIDI 文件。**",
                  "扫描 MIDI 信息 / Scan MIDI Info — actually reads the file, fills the track list into the 03 轨道选择 / Track Selection card on the right, and writes a track summary to output/current_midi_info.txt. **Press it with no file chosen and it answers 请先选择 MIDI 文件 / Please select a MIDI file first.**",
                ),
                l(
                  "虚线框 📥 将 MIDI 文件拖放到此处 —— 直接从资源管理器把文件拖到窗口上，作用与 浏览 MIDI 文件 一样（下面小字写着 支持 .mid 和 .midi 格式）。拖进来的文件如果不是这两种扩展名，窗口上会短暂报一行错误。",
                  "The dashed 📥 将 MIDI 文件拖放到此处 / Drop MIDI file here box — drag a file in from your file manager; it does exactly what the 浏览 MIDI 文件 / Browse MIDI File button does. The small print underneath reads 支持 .mid 和 .midi 格式 / Supports .mid and .midi formats. Drop anything else and a short error line appears on screen.",
                ),
              ],
            },
            {
              kind: "note",
              text: l(
                "换一个 MIDI 文件会把上一次的扫描结果和轨道勾选状态一起清掉，需要重新 扫描 MIDI 信息，这是有意的——避免拿旧文件的轨道号去解析新文件。",
                "Loading a different MIDI clears the previous scan and tick marks, so you scan again. That is intentional: it stops track numbers from the old file being applied to the new one.",
              ),
            },
            {
              kind: "p",
              text: l(
                "文件还没选时，当前文件 的位置显示的是空状态 尚未选择 MIDI 文件 / 请点击「浏览 MIDI 文件」选择一个 .mid 文件。",
                'With no file chosen, the 当前文件 / Current File slot shows the empty state 尚未选择 MIDI 文件 / No MIDI file selected, and 请点击「浏览 MIDI 文件」选择一个 .mid 文件。 / Click "Browse MIDI File" to select a .mid file.',
              ),
            },
          ],
        },
        {
          id: "midi-params",
          title: l(
            "02 参数设置：三个参数各有各的脾气",
            "02 参数设置 / Parameter Settings: three parameters, three behaviours",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "卡片上方那行说明值得照抄一遍：这些参数会直接作用于 MIDI 解析结果和临时输出文件。 也就是说，这三个值改完必须重新扫描、重新生成，结果才会跟着变。",
                "The line above the fields is worth taking literally: 这些参数会直接作用于 MIDI 解析结果和临时输出文件。 / These parameters directly affect MIDI parsing results and temporary output files. Change any of the three and you must rescan and regenerate before the result follows.",
              ),
            },
            {
              kind: "p",
              text: l(
                "第一小块 基础参数 里是两个数字框：",
                "The first sub-block, 基础参数 / Basic Parameters, holds two number boxes:",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "通道 —— **范围 -1 ~ 15，默认 -1。** 右边小字写着 -1 表示全部通道。填 -1 就是把文件里所有通道的音符都算进来；填 0 ~ 15 则只解析那一个 MIDI 通道。",
                  "通道 / Channel — **range -1 to 15, default -1.** The small print beside it reads -1 表示全部通道 / -1 means all channels. At -1 every channel in the file is parsed; set 0–15 and only that MIDI channel is read.",
                ),
                l(
                  "**什么时候动它：** 一个文件里同一轨道上叠了多个通道、你只想取其中一条时。注意 03 轨道选择 的列表是按「轨道 + 通道」列出来的，所以想知道该填几，先扫描一眼列表里的 通道 列。",
                  "**When to touch it:** when one track carries several channels and you want just one of them. Note that the 03 轨道选择 / Track Selection list holds track-plus-channel combinations, so scan once and read the 通道 / Channel column to learn which number you need.",
                ),
                l(
                  "FPS —— **范围 1 ~ 240，默认 60。** 这是输出动画的帧率：所有关键帧的帧号都按它换算。",
                  "FPS — **range 1 to 240, default 60.** This is the frame rate of the output animation; every keyframe number is converted with it.",
                ),
                l(
                  "**什么时候动它：** 目标 3D 工程的帧率不是 60 时才改。要和 Unreal 工程保持一致最省事——两边不一致的话，动画时长会整体对不上。改成 30 帧数减半、算得更快，动作细节也跟着变少；改成 120 更细，但等得更久。",
                  "**When to touch it:** only when the target 3D project is not at 60. Matching your Unreal project saves trouble — mismatched rates throw the whole timing off. Dropping to 30 halves the frames, computes faster and shows less motion detail; 120 is finer but takes longer.",
                ),
              ],
            },
            {
              kind: "p",
              text: l(
                "第二小块 量化 是一个下拉框，选项有 无量化、1/16、1/32、1/64、1/128，**默认 无量化**。",
                "The second sub-block, 量化 / Quantization, is a drop-down with 无量化 / None, 1/16, 1/32, 1/64 and 1/128; the **default is 无量化 / None**.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "**它做什么：** 量化就是给时间轴铺一张网格。选了 1/16，音符起点会被吸附到十六分音符网格上；网格里的时间点如果落了好几个音，只保留最高的那个。无量化 则完全按 MIDI 里的实际位置走。",
                  "**What it does:** quantization lays a grid over the timeline. With 1/16 the note attacks snap onto sixteenth-note positions, and when several notes land on the same grid point only the highest survives. 无量化 / None follows the MIDI's real positions exactly.",
                ),
                l(
                  "**什么时候动它：** 真人弹的 MIDI 常常前后差几毫秒，听起来「不齐」，量化一档能让节奏干净利落；但摇摆（swing）和自由速度（rubato）的曲子一量化就会变呆。**先试生成一遍，觉得节奏发飘再回来调。**",
                  "**When to touch it:** human-played MIDI often drifts a few milliseconds, which reads as sloppy; one notch of quantization tightens it up. Swing and rubato, though, go stiff the moment you quantize. **Generate once first, and come back only if the rhythm feels loose.**",
                ),
                l(
                  "**越往右网格越细：** 1/16 下手最重（动作最少、最整齐），1/128 下手最轻（接近 无量化）。不知道该选哪个就从 1/16 开始试，还觉得不够齐就说明问题不在量化上。",
                  "**Finer grids are further down the list:** 1/16 is the heaviest hand (fewest distinct attacks, tidiest result) and 1/128 the lightest, close to 无量化 / None. Start at 1/16 if you are unsure; if it still feels loose, quantization was not the problem.",
                ),
              ],
            },
          ],
        },
        {
          id: "midi-tracks",
          title: l(
            "03 轨道选择：勾出要生成动画的轨道",
            "03 轨道选择 / Track Selection: tick the tracks to animate",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这张卡片一开始是空的，顶部提示 暂无轨道信息 / 请先扫描 MIDI 以获取轨道列表。 点过 扫描 MIDI 信息 之后，这里会出现一张五行表：选择、轨道、通道、乐器、音符数。",
                "The card starts empty with 暂无轨道信息 / No track info and 请先扫描 MIDI 以获取轨道列表。 / Scan the MIDI file first to get the track list. After 扫描 MIDI 信息 / Scan MIDI Info it fills with a five-column table: 选择 / Select, 轨道 / Track, 通道 / Channel, 乐器 / Instrument and 音符数 / Notes.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "选择 —— 一个勾选框。勾上表示「这条轨道要参与动画」。",
                  '选择 / Select — a checkbox. Tick it to say "this track takes part in the animation".',
                ),
                l(
                  "轨道 —— MIDI 里的轨道号（从 0 开始数）。输出文件名里的 {轨道} 就是它。",
                  "轨道 / Track — the track number inside the MIDI (counted from 0). This is the {轨道} / {track} in the output file names.",
                ),
                l(
                  "通道 —— 这条轨道的通道号，取不到时显示 N/A。它和 02 参数设置 里的 通道 是同一个概念：想只解析某一条通道，就照这个数字去填。",
                  "通道 / Channel — the track's channel number, or N/A when it cannot be read. It is the same notion as the 通道 / Channel box in the 02 参数设置 / Parameter Settings card: to parse just one, fill in the number you see here.",
                ),
                l(
                  "乐器 —— 从 MIDI 的乐器信息里读出来的名字，例如 Acoustic Grand Piano、SynthStrings 1。**这是判断「哪条是旋律」最直接的线索**，别去数轨道号。",
                  "乐器 / Instrument — the instrument name read from the MIDI, e.g. Acoustic Grand Piano or SynthStrings 1. **This is the most direct clue to which track holds the melody** — do not go by track number.",
                ),
                l(
                  "音符数 —— 这条轨道上原始的音符总数。它是文件里的真实数量，不受 量化 或音域设置影响。",
                  "音符数 / Notes — the raw note count on that track, taken straight from the file. It is unaffected by 量化 / Quantization or the range settings.",
                ),
              ],
            },
            {
              kind: "p",
              text: l(
                "列表下面的 ✅ 添加选中的轨道 是**真正生效的那一下**：点它才会把勾选的轨道写进这次的生成配置，同时卡片底部出现绿条 已确认轨道: 3 告诉你确认了哪几条。一个都没勾就点，会提示 请至少勾选一个轨道。",
                "✅ 添加选中的轨道 / Add Selected Tracks underneath is **the click that actually counts**: it writes the ticked tracks into this run's configuration and shows a green line reading 已确认轨道 / Confirmed tracks: 3 at the bottom of the card. Press it with nothing ticked and it answers 请至少勾选一个轨道 / Please select at least one track.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l("光勾选不算，必须点按钮", "Ticking alone is not enough"),
              text: l(
                "勾选框只是编辑状态。不点 ✅ 添加选中的轨道，下面的绿条不变，生成时用的还是上一次确认的轨道。反过来，勾完之后卡片底部若是 当前未选择轨道，后续解析不会得到可用音符。，说明一条都没确认，生成必然失败。",
                "The checkboxes are only an editing state. Without ✅ 添加选中的轨道 / Add Selected Tracks the green line does not change and generation uses the previously confirmed tracks. Conversely, if the bottom of the card reads 当前未选择轨道，后续解析不会得到可用音符。 / No tracks selected; parsing will not produce usable notes, nothing is confirmed and generation is bound to fail.",
              ),
            },
            {
              kind: "p",
              text: l(
                "再下面一个按钮是 ✂️ 修剪 MIDI 音符。它不参与生成，只是**另存**一份修整过的文件：把选中轨道里重叠的音符截断、把不该连的音断开，输出到源 MIDI 旁边，文件名加 _trimmed 后缀（例如 暮雨的沫痕_trimmed.mid）。做完控制台位置会提示 预处理完成：已生成 *_trimmed.mid 文件。",
                "The button below that is ✂️ 修剪 MIDI 音符 / Trim MIDI Notes. It takes no part in generation; it only **saves a copy** with fixes: overlapping notes on the selected tracks are truncated and notes that should not be tied are separated. The result lands next to the source file with a _trimmed suffix (for example 暮雨的沫痕_trimmed.mid), and the message reads 预处理完成：已生成 *_trimmed.mid 文件 / Preprocessing done: *_trimmed.mid file generated.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "**什么时候用它：** 生成的动画里出现明显不该连的音、或者听起来像滑音的地方，往往是源文件里音符尾巴和下一个音头重叠了。这种情况先修剪，再拿 _trimmed.mid 重新选文件、重新扫描。",
                  "**When to use it:** slurred or tied-sounding notes that should not be tied usually mean note tails overlap the next attack in the source file. Trim first, then load the _trimmed.mid as a new file and scan again.",
                ),
                l(
                  "它要求至少确认一条轨道（报 请至少勾选一个轨道后再预处理），也只处理你确认过的那几条轨道，原文件本身不会被改动。",
                  "It requires at least one confirmed track, answering 请至少勾选一个轨道后再预处理 / Select at least one track before preprocessing, and only touches the tracks you confirmed. Your original file is never modified.",
                ),
              ],
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-execute",
      title: l(
        "▶ 执行操作：生成并看它跑完",
        "▶ 执行操作 / Execution: generate and watch it finish",
      ),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一屏没有新参数，只有两个按钮和一个控制台。它的作用是让你在动手之前**核对前面两屏的选择**，动手之后**看到每一步到底跑到哪了**。",
            "This screen adds no new parameters — just two buttons and a console. Its job is to let you **check the choices made on the previous two screens** before you commit, and **watch every stage** afterwards.",
          ),
        },
        {
          kind: "p",
          text: l(
            "左列是 01 当前配置 和 02 执行流程，右列是 03 控制台输出。",
            "The left column holds 01 当前配置 / Current Setup and 02 执行流程 / Workflow; the right column holds 03 控制台输出 / Console Output.",
          ),
        },
      ],
      subs: [
        {
          id: "exec-overview",
          title: l(
            "01 当前配置：生成前的最后一眼",
            "01 当前配置 / Current Setup: one last look before generating",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这张卡片把前面两屏的结果汇总成两块只读信息，加三个状态块。",
                "This card summarises the previous two screens as two read-only blocks, plus three status chips.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "资源 —— 角色 一行显示 .wind 文件名（没选角色时是 未选择角色），MIDI 一行显示 MIDI 文件名（没选时是 未选择 MIDI）。",
                  "资源 / Resources — the 角色 / Avatar row shows the .wind file name (未选择角色 / No avatar selected when none is chosen) and the MIDI row shows the MIDI file name (未选择 MIDI / No MIDI selected when none is).",
                ),
                l(
                  "参数 —— 六行：轨道、通道、FPS、音域、量化、渐变时长。它们分别来自 🎵 MIDI处理 的轨道确认与参数设置、以及 ⚙ 角色设置 的音域和渐变时长。**这一块就是你的自检清单。**",
                  "参数 / Parameters — six rows: 轨道 / Track, 通道 / Channel, FPS, 音域 / Range, 量化 / Quantization and 渐变时长 / Transition Duration. They come from the track confirmation and parameter settings on the 🎵 MIDI处理 / MIDI Processing tab and from the range and transition duration on the ⚙ 角色设置 / Avatar Settings tab. **This block is your pre-flight checklist.**",
                ),
                l(
                  "通道 这一行显示的是文字而不是数字：-1 显示 全部通道，其它值显示 指定通道。量化 显示下拉框里那个词（例如 无量化），渐变时长 带单位（例如 0.50 秒）。",
                  "The 通道 / Channel row shows words rather than a number: -1 reads 全部通道 / All channels and anything else reads 指定通道 / Specific channel. The 量化 / Quantization row shows the drop-down's wording (for example 无量化 / None) and 渐变时长 carries its unit (for example 0.50 秒 / 0.50 s).",
                ),
                l(
                  "轨道 一行如果是 未选择，后面生成一定会失败——回 🎵 MIDI处理 勾轨道并点 ✅ 添加选中的轨道。",
                  "If the 轨道 / Track row reads 未选择 / Not selected, generation will certainly fail — go back to the 🎵 MIDI处理 / MIDI Processing tab, tick a track and press ✅ 添加选中的轨道 / Add Selected Tracks.",
                ),
                l(
                  "三个状态块 初始化、指法、动画 —— 每块两种样子：灰底 …: 未完成 和绿底 …: 已完成。它们只反映**本次运行**的进度：打开软件时都是灰的，一键执行 跑完后三块全绿。换个 MIDI 或换位演奏者后想重跑，它们会随新的一轮重新开始。",
                  "The three chips are 初始化 / Init, 指法 / Fingering and 动画 / Animation, each in two states: grey …: 未完成 / Pending and green …: 已完成 / Done. They report **this session's** progress only: all grey when you launch, all green after 一键执行 / Run All finishes. Starting a new run with a different MIDI or performer resets them.",
                ),
              ],
            },
          ],
        },
        {
          id: "exec-flow",
          title: l(
            "02 执行流程：两个按钮加一个「都做」",
            "02 执行流程 / Workflow: two steps and one that does both",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "卡片里三个按钮：1. 生成指法、2. 生成动画、一键执行。生成期间按钮会变灰不可点，卡片上方出现转圈的 后台任务执行中...。",
                "The card holds three buttons: 1. 生成指法 / 1. Generate Fingering, 2. 生成动画 / 2. Generate Animation and 一键执行 / Run All. While a run is going the buttons grey out and a spinner reading 后台任务执行中... / Background task running... appears above them.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "1. 生成指法 —— 第一步：读角色配置与 MIDI，算出每个音的手指位置、口型与乐器形态，写出指法状态文件。它同时会算一个整体移调偏移，把曲子挪进乐器吹得出来的音域（这一步叫「初始化」）。",
                  "1. 生成指法 / 1. Generate Fingering — step one: reads the performer config and the MIDI, works out the finger positions, embouchure and instrument shapes for every note, and writes the fingering state file. It also computes one overall transposition to bring the piece into the instrument's playable range (this is the 初始化 / Init stage).",
                ),
                l(
                  "2. 生成动画 —— 第二步：读第一步的产物，生成左右手关键帧、口型与乐器形态关键帧、活动曲线，最后写出汇总报告。**没跑过第一步就点它会直接提示 请先生成指法，再执行动画生成。** 只想换参数重跑动画时，用它比 一键执行 省一半时间。",
                  "2. 生成动画 / 2. Generate Animation — step two: reads step one's output and produces the hand keyframes, the lip and instrument shape keyframes, the activity curve and finally the summary report. **Press it before step one and it answers 请先生成指法，再执行动画生成 / Please generate the fingering first, then generate the animation.** When you only changed something that affects the animation, it saves you half the time compared with 一键执行 / Run All.",
                ),
                l(
                  "一键执行 —— 两步连着跑：指法一完成就自动接上动画。控制台里会先打一行提示说明这点。日常用这一个按钮就够了。",
                  "一键执行 / Run All — runs both in sequence: as soon as the fingering finishes, the animation starts. The console prints a line saying so. For everyday use this is the only button you need.",
                ),
                l(
                  "三个按钮在任务运行期间都点不动。硬要并发也没有意义——正在跑的时候再触发，控制台只会回一句 已有后台任务正在执行，请等待当前任务结束。",
                  "None of the three responds while a task is running. Forcing concurrency buys nothing: a second trigger just draws 已有后台任务正在执行，请等待当前任务结束 / A background task is already running, please wait for it to finish in the console.",
                ),
              ],
            },
            {
              kind: "note",
              text: l(
                "生成过程中可以随便切页签去看别的，任务在后台继续跑。切回 ▶ 执行操作 时控制台会补上期间攒下的日志。",
                "You can switch tabs freely while it runs; the task keeps going in the background, and the console fills in the accumulated log lines when you come back.",
              ),
            },
            {
              kind: "callout",
              tone: "tip",
              title: l("怎么判断跑没跑完", "Telling whether it finished"),
              text: l(
                "看 03 控制台输出 顶部那行状态字：状态：运行中 是还在跑，状态：空闲 才是结束（不论成功还是出错）。绿色横幅里的 WindRise报告已保存到: … 是成功收尾的标志。",
                "Watch the status text at the top of 03 控制台输出 / Console Output. 状态：运行中 / Status: Running means it is still working; 状态：空闲 / Status: Idle means it stopped, whether it succeeded or failed. The green line WindRise报告已保存到 / WindRise report saved to: … marks a successful finish.",
              ),
            },
          ],
        },
        {
          id: "exec-console",
          title: l(
            "03 控制台输出：出错时先看这里",
            "03 控制台输出 / Console Output: the first place to look when something is off",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "右边整列都是控制台。顶部三个按钮 复制结果、复制全部、清空日志，下面一行小字显示 日志 N 行 和当前状态，再往下就是逐行滚动的输出，带颜色的行分别代表不同性质：绿色是成功、橙色是提醒、红色是错误。",
                "The whole right column is the console. Three buttons sit at the top — 复制结果 / Copy Result, 复制全部 / Copy All and 清空日志 / Clear Log — then a small line showing 日志 N 行 / N log lines and the current status, and below that the scrolling output, where colour carries meaning: green for success, orange for warnings, red for errors.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "复制结果 —— 把**最近一次生成的结果文件完整路径**拷进剪贴板，方便直接粘到 Blender 或 Unreal 的加载框里。还没有结果时它会回一句 尚未找到可复制的结果路径。",
                  "复制结果 / Copy Result — copies the **full path of the most recent result file** to the clipboard, ready to paste into the Blender or Unreal load field. Before any result exists it answers 尚未找到可复制的结果路径 / No result path to copy yet.",
                ),
                l(
                  "复制全部 —— 把整个控制台文本拷走，遇到看不懂的报错可以整段发给作者。控制台是空的时候会提示 控制台无内容可复制。",
                  "复制全部 / Copy All — copies the whole console as text, handy for sending an unfamiliar error to the author. With an empty console it answers 控制台无内容可复制 / Console has no content to copy.",
                ),
                l(
                  "清空日志 —— 清掉当前所有行，让下一轮从干净的地方开始。清空不影响已经生成的文件，只影响显示。",
                  "清空日志 / Clear Log — clears every line so the next run starts clean. It affects the display only; files already generated are untouched.",
                ),
              ],
            },
            {
              kind: "p",
              text: l(
                "一次正常生成按顺序会经过这些阶段，日志里的措辞也差不多就是这些：开始初始化系统 → 已加载角色配置 → 正在计算最佳移调偏移 → 最佳移调偏移（有效音域）→ 开始解析 MIDI → MIDI 解析得到若干个音符段 → 开始生成指法状态序列 → 指法状态已导出到 → 指法生成完成 → 开始动画生成 → 生成左手动画 / 右手动画 / 人物 Shape Key 动画 / 乐器 Shape Key 动画 / 活动曲线 → 保存最终汇总文件 → 汇总文件已保存 → 动画生成完成 → WindRise报告已保存到。",
                "A clean run passes through these stages in order, and the log wording matches: initialising the system, loading the performer config, computing the optimal transposition, reporting it with the effective range, starting MIDI parsing, reporting how many note segments came out, building the fingering state sequence, exporting it, finishing the fingering, starting animation generation, then left hand / right hand / character shape keys / instrument shape keys / activity curve, then saving the final summary, reporting where the summary went, finishing the animation, and finally reporting the report path.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "指法生成完成: … 与 移调偏移值: N —— 第一步结束。移调偏移值就是软件为了迁就乐器音域把整首曲子挪了几个半音，冷门调式的曲子这里可能是负数。",
                  "指法生成完成 / Fingering generation complete and 移调偏移值 / Transposition offset: N — step one is done. The offset is how many semitones the whole piece was shifted to suit the instrument's range; unusual keys can push it negative.",
                ),
                l(
                  "WindRise报告已保存到: … —— 全部完成，这个路径就是你要交给插件的文件。",
                  "WindRise报告已保存到 / WindRise report saved to: … — everything is done, and that path is the file you hand to the plugin.",
                ),
                l(
                  "错误: 未选择角色配置文件 / 错误: 未选择 MIDI 文件 / 错误: 未选择任何 MIDI 轨道 —— 三个最常见的前置条件缺失，分别回 ⚙ 角色设置、🎵 MIDI处理 补齐。",
                  "错误: 未选择角色配置文件 / Error: no avatar config file selected, 错误: 未选择 MIDI 文件 / Error: no MIDI file selected and 错误: 未选择任何 MIDI 轨道 / Error: no MIDI tracks selected — the three most common missing prerequisites; fix them on the ⚙ 角色设置 / Avatar Settings and 🎵 MIDI处理 / MIDI Processing tabs respectively.",
                ),
              ],
            },
            {
              kind: "note",
              text: l(
                "管乐是单音乐器，同一时刻的多个音符只会留下最高的那个，所以 MIDI 解析得到 N 个音符段 里的 N 常常比源文件的音符总数少不少——这是正常现象，不是丢音。",
                "Wind instruments are monophonic, so simultaneous notes keep only the highest. The N in MIDI 解析得到 N 个音符段 / MIDI parsed into N note segment(s) is therefore often well below the source file's raw note count — that is expected, not lost notes.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "defaults",
      title: l("「什么都不用改」清单", "What you can safely leave alone"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "下面这张表可以当默认值速查用：第一列是参数，第二列是它出厂时的样子，第三列告诉你什么时候才真的需要动它。",
            "Treat the table below as a defaults cheat sheet: the parameter, how it ships, and the only situations that justify changing it.",
          ),
        },
        {
          kind: "table",
          head: [
            l("参数", "Parameter"),
            l("默认值", "Default"),
            l("什么时候才需要动", "Only change it when"),
          ],
          rows: [
            [
              l("角色选择", "角色选择 / Avatar Selection"),
              l("名单里的第一位演奏者", "the first performer in the list"),
              l(
                "每次都要选——挑成你要用的那位。",
                "Every time — pick the one you actually want.",
              ),
            ],
            [
              l("渐变时长(秒)", "渐变时长(秒) / Transition Duration (s)"),
              l("0.50 秒（范围 0.05 ~ 2.0）", "0.50 s (range 0.05–2.0)"),
              l(
                "动作发糊（往小调）或发硬（往大调），慢曲子可试 0.8 ~ 1.2。",
                "Motion smears (lower it) or looks stiff (raise it); for slow pieces try 0.8–1.2.",
              ),
            ],
            [
              l(
                "最小音高 / 最大音高",
                "最小音高 / Min Note and 最大音高 / Max Note",
              ),
              l(
                "跟随所选演奏者的 .wind（例如 60 / 91）",
                "whatever the selected performer's .wind declares (e.g. 60 / 91)",
              ),
              l(
                "基本不用动，改了也不影响生成结果，只影响这里显示的数字。",
                "Rarely at all — changing them does not affect the result, only the numbers shown.",
              ),
            ],
            [
              l("通道", "通道 / Channel"),
              l("-1（全部通道）", "-1 (all channels)"),
              l(
                "只想解析某一个通道时，按 03 轨道选择 里 通道 列的数字填。",
                "When you want just one channel — use the number in the 通道 / Channel column of 03 轨道选择 / Track Selection.",
              ),
            ],
            [
              l("FPS", "FPS"),
              l("60（范围 1 ~ 240）", "60 (range 1–240)"),
              l(
                "目标 3D 工程不是 60 帧时，两边对齐。",
                "When your 3D project is not at 60 fps — match them.",
              ),
            ],
            [
              l("量化", "量化 / Quantization"),
              l("无量化", "无量化 / None"),
              l(
                "节奏听着发飘时试 1/16；摇摆、自由速度的曲子保持 无量化。",
                "Rhythm feels loose — try 1/16. Keep 无量化 / None for swing and rubato.",
              ),
            ],
            [
              l("轨道勾选", "Track ticks"),
              l(
                "未确认（列表首条预勾选）",
                "unconfirmed (first row pre-ticked)",
              ),
              l(
                "每次都要勾旋律那条，并点 ✅ 添加选中的轨道。",
                "Every time — tick the melody track and press ✅ 添加选中的轨道 / Add Selected Tracks.",
              ),
            ],
            [
              l(
                "换气时长、活动曲线时长",
                "Breath and activity-curve durations",
              ),
              l(
                "程序内固定值，界面无控件",
                "fixed inside the app, no UI control",
              ),
              l("改不了。", "You cannot."),
            ],
          ],
        },
        {
          kind: "note",
          text: l(
            "只有 渐变时长(秒) 和 量化 会明显改变成片的观感，其余都是「对齐环境」或「记录信息」性质的设置。",
            "Only 渐变时长(秒) / Transition Duration (s) and 量化 / Quantization visibly change the finished animation; the rest are about matching your environment or recording information.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "faq",
      title: l("参数常见疑问", "Parameter questions people actually ask"),
      blocks: [
        {
          kind: "callout",
          tone: "info",
          title: l(
            "改完参数要重新扫描吗？",
            "Do I need to rescan after changing a parameter?",
          ),
          text: l(
            "通道、FPS、量化 改完要再点一次 扫描 MIDI 信息，并且重新生成，结果才会跟着变。渐变时长(秒) 只影响生成阶段——改完直接回 ▶ 执行操作 重新跑动画就行，不用动 MIDI 那一屏。",
            "The 通道 / Channel, FPS and 量化 / Quantization values need another 扫描 MIDI 信息 / Scan MIDI Info and a fresh generation before the result follows. 渐变时长(秒) / Transition Duration (s) only affects generation, so change it and re-run the animation from ▶ 执行操作 / Execution without touching the MIDI screen.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "动手调参之前，先确认是哪一类问题",
            "Before tuning, decide which kind of problem you have",
          ),
          text: l(
            "**节奏不齐**看 量化；**动作发糊或发硬**看 渐变时长(秒)；**音高整体不对**看 最佳移调偏移 那行日志（这是软件自动决定的，界面改不了）；**少了音**先确认轨道勾选和 已确认轨道，再看是不是单音合并造成的。四类问题各修各的，别一起改。",
            "**Uneven rhythm** points at 量化 / Quantization; **smeared or stiff motion** at 渐变时长(秒) / Transition Duration (s); **wrong overall pitch** at the 最佳移调偏移 / Best transposition offset log line (the app decides that itself and the UI cannot override it); **missing notes** first at your track ticks and 已确认轨道 / Confirmed tracks, then at monophonic merging. Fix one category at a time.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "为什么音域明明够，还是要移调？",
            "The range looks fine — why transpose anyway?",
          ),
          text: l(
            "音域 是这件乐器物理上吹得出来的范围，而每首曲子的实际音高分布不同。软件会挑一个能覆盖最多音的音域位置，控制台里的 最佳移调偏移 与 有效音域 就是这个结果。听起来没跑调就说明它算对了。",
            "音域 / Range is what the instrument can physically play, while every piece sits differently inside it. The app picks the placement that covers the most notes; that is what the 最佳移调偏移 / Best transposition offset line reports in the console, along with the effective range. If it does not sound out of key, it got it right.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "同一个 MIDI，换 FPS 会变成新文件吗？",
            "Does a different FPS make a new file?",
          ),
          text: l(
            "不会。输出文件名只由角色、MIDI、轨道拼成，所以改 FPS 重跑是**覆盖**上一次的结果。想留着对比，先把上一轮的 output 目录复制一份出来。",
            "No. Output names are built only from performer, MIDI and track, so re-running at a different FPS **overwrites** the previous result. Copy the output folder aside first if you want both.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "最小音高 / 最大音高 改了没反应，是不是坏了？",
            "The 最小音高 / Min Note and 最大音高 / Max Note boxes changed nothing — is it broken?",
          ),
          text: l(
            "没坏。这两个框当前的作用是记录与核对这位演奏者的音域，会显示在 ▶ 执行操作 的 参数 里；真正决定音高的是软件按乐器物理音域自动算出的移调偏移。想让这两个数字跟着变，换一位演奏者最直接。",
            "Nothing is broken. Today these two boxes record and display the performer's range under 参数 / Parameters on ▶ 执行操作 / Execution; the pitch you actually get is decided by the transposition the app computes from the instrument's physical range. Switching performer is the direct way to move these numbers.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "想给同一个角色出两个不同版本",
            "Two versions from the same performer",
          ),
          text: l(
            "最省事的办法是改一次 量化 或 渐变时长(秒) 生成一版、把 output 拷走，再改回去生成另一版。因为文件名不含参数，两版不能同时留在同一个 output 目录里。",
            "Easiest path: generate with one 量化 / Quantization or 渐变时长(秒) / Transition Duration (s) setting, copy output aside, then change it back and generate the other. File names carry no parameters, so both versions cannot coexist in one output folder.",
          ),
        },
      ],
    },
  ],
};
